import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, supabase } from '../lib/supabase';
import { usePricing } from '../lib/pricing';
import { scorePassword } from '../lib/password';
import PasswordInput from '../components/PasswordInput';
import './SignIn.css';

const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,28}[a-z0-9])?$/;
const OTP_RE = /^\d{6}$/;

const SignUp = () => {
  const { pricing } = usePricing();
  const navigate = useNavigate();
  const [signupsOpen, setSignupsOpen] = useState(null);   // null=loading, true/false=known
  const [stage, setStage] = useState('form');             // form | otp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [shopName, setShopName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [plan, setPlan] = useState('standard');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [emailTaken, setEmailTaken] = useState(false);
  const pwScore = scorePassword(ownerPassword);

  // Subdomain live check
  const [subStatus, setSubStatus] = useState('idle');   // idle|checking|ok|taken|invalid

  // OTP stage
  const [intentId, setIntentId] = useState(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('platform_settings')
        .select('signups_open')
        .limit(1)
        .maybeSingle();
      setSignupsOpen(!!data?.signups_open);
    })();
  }, []);

  const checkSubdomain = async (raw) => {
    const s = (raw || '').trim().toLowerCase();
    if (!s) { setSubStatus('idle'); return; }
    if (!SUBDOMAIN_RE.test(s)) { setSubStatus('invalid'); return; }
    setSubStatus('checking');
    const { data } = await supabase.from('shops').select('id').eq('subdomain', s).limit(1);
    setSubStatus((data && data.length) ? 'taken' : 'ok');
  };

  const formValid =
    shopName.trim().length >= 2 &&
    subStatus === 'ok' &&
    ownerName.trim().length >= 2 &&
    /@/.test(ownerEmail) &&
    !emailTaken &&
    ownerPhone.trim().length >= 6 &&
    pwScore.level !== 'weak';

  const submitStart = async (e) => {
    e.preventDefault();
    if (!formValid) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/self/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_name: shopName.trim(),
          subdomain: subdomain.trim().toLowerCase(),
          plan,
          owner_name: ownerName.trim(),
          owner_email: ownerEmail.trim().toLowerCase(),
          owner_password: ownerPassword,
          owner_phone: ownerPhone.trim(),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 409 && /already exists/i.test(json.detail || '')) {
        setEmailTaken(true);
        setError('');
        return;
      }
      if (!res.ok) throw new Error(json.detail || `HTTP ${res.status}`);
      setIntentId(json.intent_id);
      setStage('otp');
    } catch (e2) {
      setError(e2.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    if (!OTP_RE.test(code)) { setError('Enter the 6-digit code from your email.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/self/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intent_id: intentId, code: code.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || `HTTP ${res.status}`);

      // Auto-sign-in with the password the user just chose
      const { error: signErr } = await signIn(ownerEmail.trim().toLowerCase(), ownerPassword);
      if (signErr) {
        // Fall back to a helpful redirect if auto-signin fails
        navigate('/signin');
        return;
      }
      navigate('/welcome');
    } catch (e2) {
      setError(e2.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (signupsOpen === false) {
    return (
      <div className="auth">
        <div className="auth-l">
          <div className="brandrow"><img className="mark" src="/logos/instacut-mark-28.svg" alt="" width="28" height="28" />Instacut</div>
          <h3>Signups are<br />currently closed.</h3>
          <p>New shops are being onboarded by hand while we scale. Get in touch and we&apos;ll set you up personally.</p>
        </div>
        <div className="auth-r">
          <h4>Not open yet</h4>
          <div className="s">Public self-serve signup is off. Contact <a href="mailto:hi@instacut.co.uk">hi@instacut.co.uk</a> and we&apos;ll onboard you.</div>
          <div style={{ marginTop: 20 }}>
            <a href="/signin" className="btn s">Sign in</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth" data-testid="signup-page">
      <div className="auth-l">
        <div className="streak" style={{ top: '30%', right: '-20px', width: '240px' }}></div>
        <div className="streak" style={{ top: 'calc(30% + 12px)', right: '-20px', width: '160px', opacity: '0.5' }}></div>
        <div className="brandrow">
          <img className="mark" src="/logos/instacut-mark-28.svg" alt="" width="28" height="28" />
          Instacut
        </div>
        <h3>Start taking<br />bookings today.</h3>
        <p>Stand up your own branded booking page, take deposits and cut no-shows. £{pricing.standard.price}/mo Standard, £{pricing.premium.price}/mo Premium.</p>
        <div className="quote">&ldquo;Cut my no-shows in half in the first month.&rdquo; — Marcus, Sharp &amp; Sons</div>
      </div>

      <div className="auth-r">
        {stage === 'form' && (
          <>
            <h4>Create your shop</h4>
            <div className="s">Two minutes. Verify by email. No card needed yet.</div>

            {error && <div className="error-message" data-testid="signup-error">{error}</div>}

            <form onSubmit={submitStart}>
              <div className="field">
                <label>Shop name</label>
                <input className="inp" value={shopName} onChange={(e) => setShopName(e.target.value)} required disabled={loading} data-testid="signup-shop-name" />
              </div>
              <div className="field">
                <label>Subdomain <small style={{ color: 'var(--mute)', fontWeight: 400 }}>.instacut.co.uk</small></label>
                <input
                  className="inp"
                  value={subdomain}
                  onChange={(e) => { const v = e.target.value.toLowerCase(); setSubdomain(v); checkSubdomain(v); }}
                  placeholder="sharpandsons"
                  required
                  disabled={loading}
                  data-testid="signup-subdomain"
                />
                <SubStatus status={subStatus} />
              </div>
              <div className="field">
                <label>Plan</label>
                <select className="inp" value={plan} onChange={(e) => setPlan(e.target.value)} disabled={loading} data-testid="signup-plan">
                  <option value="standard">Standard — £{pricing.standard.price}/mo</option>
                  <option value="premium">Premium — £{pricing.premium.price}/mo (+£{pricing.extraBarberPrice}/mo per extra barber)</option>
                </select>
              </div>
              <div className="field">
                <label>Your name</label>
                <input className="inp" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} required disabled={loading} data-testid="signup-owner-name" />
              </div>
              <div className="field">
                <label>Email</label>
                <input
                  className={`inp ${emailTaken ? 'has-error' : ''}`}
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => { setOwnerEmail(e.target.value); if (emailTaken) setEmailTaken(false); }}
                  required
                  disabled={loading}
                  data-testid="signup-email"
                />
                {emailTaken && (
                  <div className="field-error" data-testid="signup-email-taken">
                    An account already exists for this email.{' '}
                    <a href="/signin" data-testid="signup-signin-link">Sign in instead →</a>
                  </div>
                )}
              </div>
              <div className="field">
                <label>Mobile</label>
                <input className="inp" type="tel" required value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} placeholder="+447700900123" disabled={loading} data-testid="signup-phone" />
              </div>
              <div className="field">
                <label>Password <small style={{ color: 'var(--mute)', fontWeight: 400 }}>min 8 chars, mix of cases + numbers</small></label>
                <PasswordInput
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  required
                  disabled={loading}
                  data-testid="signup-password"
                  toggleTestId="signup-password-toggle"
                />
                {ownerPassword.length > 0 && (
                  <div className="pw-meter" data-testid="signup-pw-meter">
                    <div className={`pw-bar ${pwScore.level}`} role="progressbar" aria-label="Password strength">
                      <span className="seg s1" />
                      <span className="seg s2" />
                      <span className="seg s3" />
                    </div>
                    <div className={`pw-label ${pwScore.level}`} data-testid="signup-pw-label">
                      {pwScore.label}
                    </div>
                    <ul className="pw-checks">
                      <li className={pwScore.checks.length8 ? 'ok' : ''}>8+ characters</li>
                      <li className={pwScore.checks.lower ? 'ok' : ''}>lowercase letter</li>
                      <li className={pwScore.checks.upper ? 'ok' : ''}>uppercase letter</li>
                      <li className={pwScore.checks.digit ? 'ok' : ''}>number</li>
                      <li className={pwScore.checks.symbol ? 'ok' : ''}>symbol</li>
                    </ul>
                  </div>
                )}
              </div>
              <button
                className="btn p"
                type="submit"
                disabled={!formValid || loading}
                style={{ width: '100%', justifyContent: 'center', padding: '12px', marginTop: 8 }}
                data-testid="signup-submit"
              >
                {loading ? 'Sending code…' : 'Continue'}
              </button>
            </form>

            <div style={{ textAlign: 'center', fontSize: '12.5px', color: 'var(--mute)', marginTop: '18px' }}>
              Already have an account? <a href="/signin" style={{ color: 'var(--acc)', fontWeight: 600, textDecoration: 'none' }}>Sign in →</a>
            </div>
          </>
        )}

        {stage === 'otp' && (
          <>
            <h4>Check your email</h4>
            <div className="s">We sent a 6-digit code to <b>{ownerEmail}</b>. It expires in 10 minutes.</div>

            {error && <div className="error-message" data-testid="signup-otp-error">{error}</div>}

            <form onSubmit={submitVerify}>
              <div className="field">
                <label>Verification code</label>
                <input
                  className="inp"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  disabled={loading}
                  style={{ fontFamily: 'var(--ff-mono)', letterSpacing: 4, fontSize: 18, textAlign: 'center' }}
                  data-testid="signup-otp-code"
                />
              </div>
              <button
                className="btn p"
                type="submit"
                disabled={loading || code.length !== 6}
                style={{ width: '100%', justifyContent: 'center', padding: '12px' }}
                data-testid="signup-otp-submit"
              >
                {loading ? 'Verifying…' : 'Verify & create shop'}
              </button>
            </form>

            <div style={{ textAlign: 'center', fontSize: '12.5px', color: 'var(--mute)', marginTop: '18px' }}>
              Wrong email? <button type="button" onClick={() => { setStage('form'); setError(''); setCode(''); }} style={{ background: 'none', border: 'none', color: 'var(--acc)', fontWeight: 600, cursor: 'pointer', padding: 0 }} data-testid="signup-otp-back">
                Go back &amp; edit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SubStatus = ({ status }) => {
  if (status === 'idle')     return null;
  if (status === 'checking') return <small style={{ color: 'var(--mute)' }}>Checking…</small>;
  if (status === 'invalid')  return <small style={{ color: '#b03a2e' }}>2–30 chars: lowercase letters, numbers, dashes.</small>;
  if (status === 'taken')    return <small style={{ color: '#b03a2e' }}>Already taken.</small>;
  if (status === 'ok')       return <small style={{ color: '#059669' }}>Available</small>;
  return null;
};

export default SignUp;
